import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useScene } from '../scene/store'
import type { SceneConfig } from '../scene/config'
import { kanOffline, laadAlles, mb, offlineStatus, wisAlles, type OfflineStatus, type Voortgang } from '../kiosk/offline'
import { logUit, useBeheer } from '../beheer/api'
import { Inloggen } from '../beheer/Inloggen'
import { Monitoring } from '../beheer/Monitoring'
import '../beheer/beheer.css'

/** De twee delen van het beheer, na het inloggen. */
type Deel = 'monitoring' | 'scene'

/** Zo lang mag het paneel onaangeroerd openstaan. */
const PANEEL_STIL = 120_000

type Tab = 'licht' | 'effecten' | 'oppervlak' | 'magazijn' | 'objecten' | 'materialen' | 'animatie'

const TABS: { id: Tab; naam: string }[] = [
  { id: 'licht', naam: 'Licht' },
  { id: 'effecten', naam: 'Effecten' },
  { id: 'oppervlak', naam: 'Oppervlak' },
  { id: 'magazijn', naam: 'Magazijn' },
  { id: 'objecten', naam: 'Objecten' },
  { id: 'materialen', naam: 'Kleuren' },
  { id: 'animatie', naam: 'Animatie' },
]

/* ---------- Bouwstenen ---------- */

function Schuif({
  label, waarde, min, max, stap = 0.01, eenheid, onWijzig,
}: {
  label: string; waarde: number; min: number; max: number
  stap?: number; eenheid?: string; onWijzig: (v: number) => void
}) {
  return (
    <label className="inst-rij">
      <span className="inst-label">{label}</span>
      <input
        className="inst-schuif"
        type="range"
        min={min} max={max} step={stap} value={waarde}
        onChange={(e) => onWijzig(parseFloat(e.target.value))}
      />
      <span className="inst-waarde">
        {stap >= 1 ? waarde : waarde.toFixed(stap < 0.01 ? 4 : 2)}{eenheid ?? ''}
      </span>
    </label>
  )
}

function Schakel({ label, aan, onWijzig }: { label: string; aan: boolean; onWijzig: (v: boolean) => void }) {
  return (
    <button
      type="button"
      className={`inst-schakel${aan ? ' inst-aan' : ''}`}
      onClick={() => onWijzig(!aan)}
      aria-pressed={aan}
    >
      <span className="inst-vink" aria-hidden="true" />
      <span>{label}</span>
    </button>
  )
}

function Kleur({ label, waarde, onWijzig }: { label: string; waarde: string; onWijzig: (v: string) => void }) {
  return (
    <label className="inst-rij">
      <span className="inst-label">{label}</span>
      <input
        className="inst-kleur"
        type="color"
        value={waarde}
        onChange={(e) => onWijzig(e.target.value)}
      />
      <span className="inst-waarde inst-mono">{waarde}</span>
    </label>
  )
}


/**
 * Offline (PLAN.md §0.11): laadt alle films en de applicatie in de cache van de
 * service worker, zodat de kiosk op beurswifi niet hapert en zonder netwerk
 * blijft werken. Eén keer bij de standopbouw, op een goede verbinding.
 */
function Offline() {
  const [status, setStatus] = useState<OfflineStatus | null>(null)
  const [voortgang, setVoortgang] = useState<Voortgang | null>(null)
  const [fouten, setFouten] = useState<string[]>([])
  const bezig = voortgang !== null

  const ververs = useCallback(() => {
    offlineStatus().then(setStatus).catch(() => setStatus(null))
  }, [])
  useEffect(ververs, [ververs])

  // De lezer meldt elk brokje; de balk hoeft niet vaker dan per beeld bij.
  const laatste = useRef<Voortgang | null>(null)
  const gepland = useRef(false)
  const meld = (v: Voortgang) => {
    laatste.current = v
    if (gepland.current) return
    gepland.current = true
    requestAnimationFrame(() => {
      gepland.current = false
      setVoortgang(laatste.current)
    })
  }

  const laad = async () => {
    setFouten([])
    meld({ bestand: '', stap: 0, stappen: 1, deel: 0, detail: 'lijst ophalen…' })
    try {
      const { fouten } = await laadAlles(meld)
      setFouten(fouten)
    } finally {
      // Na de laatste rAF, anders zet die de balk weer neer.
      requestAnimationFrame(() => requestAnimationFrame(() => setVoortgang(null)))
      ververs()
    }
  }

  const wis = async () => {
    await wisAlles()
    ververs()
  }

  if (!kanOffline()) {
    return (
      <section className="inst-profiel">
        <span className="inst-label">Offline</span>
        <p className="inst-noot">Deze browser kan niets offline bewaren.</p>
      </section>
    )
  }

  const totaal = voortgang ? Math.min(1, (voortgang.stap + voortgang.deel) / Math.max(1, voortgang.stappen)) : 0
  const compleet = status !== null && status.films === status.totaal

  return (
    <section className="inst-profiel inst-offline">
      <span className="inst-label">Offline</span>
      <p className="inst-noot">
        Films offline: <b>{status ? `${status.films}/${status.totaal}` : '…'}</b>
        {status && status.bytes > 0 ? ` (${mb(status.bytes)})` : ''}
        {status ? `, zweefronde ${status.zweeffilm !== null ? `offline (${mb(status.zweeffilm)})` : 'niet offline'}` : ''}
        {status?.laatst ? ` — geladen ${status.laatst.toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
        {status && status.gebruik !== null && status.ruimte !== null
          ? `. Opslag ${mb(status.gebruik)} van ${mb(status.ruimte)}`
          : ''}
        {status ? (status.blijvend ? ', blijvend.' : ', niet blijvend: de browser mag hem opruimen.') : ''}
        {status && !status.actief ? ' De service worker bedient deze pagina nog niet; herlaad na het laden.' : ''}
        {!compleet && !bezig ? ' Laad alles bij de standopbouw, op een goede verbinding.' : ''}
      </p>
      {bezig && (
        <div className="inst-balk" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(totaal * 100)}>
          <span style={{ width: `${totaal * 100}%` }} />
        </div>
      )}
      {bezig && voortgang && (voortgang.bestand || voortgang.detail) && (
        <p className="inst-noot inst-mono">
          {voortgang.bestand && `${Math.min(voortgang.stap + 1, voortgang.stappen)}/${voortgang.stappen} ${voortgang.bestand}`}
          {voortgang.bestand && voortgang.detail ? ' — ' : ''}
          {voortgang.detail}
        </p>
      )}
      {fouten.length > 0 && (
        <p className="inst-noot inst-fout">Niet gelukt: {fouten.join(', ')}. Probeer opnieuw.</p>
      )}
      <div className="inst-knoppen">
        <button className="inst-actie" onClick={laad} disabled={bezig}>
          {bezig ? `Laden… ${Math.round(totaal * 100)}%` : compleet ? 'Opnieuw offline laden' : 'Alles offline laden'}
        </button>
        <button className="inst-actie inst-secundair" onClick={wis} disabled={bezig}>Wissen</button>
      </div>
    </section>
  )
}

/* ---------- Het paneel ---------- */

export function Instellingen() {
  const open = useScene((s) => s.paneelOpen)
  const zetPaneel = useScene((s) => s.zetPaneel)
  const scene = useScene((s) => s.scene)
  const zet = useScene((s) => s.zet)
  const herstel = useScene((s) => s.herstel)
  const herstelGroep = useScene((s) => s.herstelGroep)
  const exporteer = useScene((s) => s.exporteer)
  const profiel = useScene((s) => s.profiel)
  const profielBron = useScene((s) => s.profielBron)
  const gemetenFps = useScene((s) => s.gemetenFps)
  const zetProfiel = useScene((s) => s.zetProfiel)
  const meetOpnieuw = useScene((s) => s.meetOpnieuw)

  const [tab, setTab] = useState<Tab>('licht')
  const [gekopieerd, setGekopieerd] = useState(false)
  const [deel, setDeel] = useState<Deel>('monitoring')

  /* Het beheer zit achter een wachtwoord (22 oktober, docs/beheer.md). De
     sessie leeft alleen zolang het paneel open is: dicht is uitgelogd, zodat
     een bezoeker na de standbeheerder het gebaar niet kan herhalen en zo
     binnen is. */
  const token = useBeheer((s) => s.token)
  const zetToken = useBeheer((s) => s.zet)
  useEffect(() => {
    if (open || !token) return
    void logUit(token)
    zetToken(null)
  }, [open, token, zetToken])
  const uitgelogd = useCallback(() => zetToken(null), [zetToken])

  // Een vergeten paneel sluit vanzelf, en logt daarmee uit (PLAN.md
  // §4.10.4): twee minuten zonder tik of toets.
  useEffect(() => {
    if (!open) return
    let klok = window.setTimeout(() => zetPaneel(false), PANEEL_STIL)
    const opnieuw = () => {
      window.clearTimeout(klok)
      klok = window.setTimeout(() => zetPaneel(false), PANEEL_STIL)
    }
    window.addEventListener('pointerdown', opnieuw, { passive: true })
    window.addEventListener('keydown', opnieuw)
    return () => {
      window.clearTimeout(klok)
      window.removeEventListener('pointerdown', opnieuw)
      window.removeEventListener('keydown', opnieuw)
    }
  }, [open, zetPaneel])

  /* Het gebaar dat het paneel opent: linksboven drie seconden vasthouden.
     Loslaten, wegglijden of een onderbreking annuleert het, zodat een bezoeker
     die de hoek aantikt niets merkt. */
  const gebaar = useRef<number | null>(null)

  const stopGebaar = useCallback(() => {
    if (gebaar.current !== null) {
      window.clearTimeout(gebaar.current)
      gebaar.current = null
    }
  }, [])

  const startGebaar = useCallback(() => {
    stopGebaar()
    gebaar.current = window.setTimeout(() => {
      gebaar.current = null
      zetPaneel(true)
    }, 3_000)
  }, [stopGebaar, zetPaneel])

  // Een lopende teller mag een unmount niet overleven.
  useEffect(() => stopGebaar, [stopGebaar])

  const kopieer = async () => {
    try {
      await navigator.clipboard.writeText(exporteer())
      setGekopieerd(true)
      setTimeout(() => setGekopieerd(false), 1800)
    } catch {
      // Klembord kan geblokkeerd zijn; download dan als bestand.
      const blob = new Blob([exporteer()], { type: 'application/json' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = 'scene.json'
      a.click()
      URL.revokeObjectURL(a.href)
    }
  }

  const l = scene.licht
  const e = scene.effecten
  const m = scene.magazijn
  const o = scene.objecten
  const op = scene.oppervlak
  const mt = scene.materialen
  const a = scene.animatie

  const objectRijen: [keyof SceneConfig['objecten'], string][] = [
    ['stellingen', 'Stellingen'], ['schoren', 'Schoren'],
    ['aanrijdbeveiliging', 'Aanrijdbeveiliging'], ['lading', 'Lading op pallets'],
    ['plafond', 'Plafondconstructie'], ['sprinklers', 'Sprinklerleiding'],
    ['kabelgoten', 'Kabelgoten'], ['dockdeuren', 'Dockdeuren'],
    ['bollards', 'Bollards'], ['vangrails', 'Vangrails'],
    ['laadstraat', 'Laadstraat'], ['transportband', 'Transportband'],
    ['palletstapels', 'Palletstapels'], ['rolcontainers', 'Rolcontainers'],
    ['afvalbakken', 'Afvalbakken'], ['bebording', 'Bebording'],
    ['vloermarkering', 'Vloermarkering'], ['stof', 'Stofdeeltjes'],
    ['wikkelaar', 'Stretchwikkelaar'], ['gangpadborden', 'Gangpadborden'],
    ['nooduitgangen', 'Nooduitgangen'], ['mezzanine', 'Mezzanine'],
    ['spiegels', 'Veiligheidsspiegels'], ['displayborden', 'Displayborden'],
    ['stillages', 'Stillages'], ['lichtlijnen', 'Rode lichtlijnen'],
    ['machines', 'Machines (trucks, pendel, schrob, wikkelaar)'],
    ['heftruck', 'C-Matic-palletpendel'],
    ['schrobmachine', 'Schrobmachine'], ['lindeBeeld', 'Linde-beeld (renders)'],
    ['zweeffilm', 'Hoofdscherm als film'],
  ]

  const animatieRijen: [keyof SceneConfig['animatie'], string][] = [
    ['trucks', 'Rijdende trucks'], ['mast', 'Mast heft'],
    ['transportband', 'Transportband'], ['dockdeuren', 'Dockdeuren'],
    ['bakens', 'Zwaailichten'], ['ventilatoren', 'Ventilatoren'],
    ['laadindicatie', 'Laadindicatie'], ['stof', 'Stofdrift'],
    ['wikkelaar', 'Wikkelaar draait'], ['heftruck', 'C-Matic rijdt'],
    ['displays', 'Displayborden'], ['zwaailicht', 'Zwaailicht'],
    ['cameraDrift', 'Camera-drift'],
  ]

  return (
    <>
      {/* Geen knop in beeld. Hij stond hiervoor als tandwiel rechtsboven, pal
          naast de taalknop: twee identiek ogende ronde knoppen naast elkaar,
          waarvan er één een Nederlandstalig beheerpaneel opent. Op een onbemande
          stand is dat het enige element waarmee een bezoeker de installatie
          zichtbaar kan laten haperen.

          In plaats daarvan: linksboven drie seconden vasthouden. Linksboven,
          want rechtsboven staat de taalknop, en dat is precies de hoek die je
          niet per ongeluk raakt terwijl je de tegels onderin bedient. */}
      <div
        className="kiosk-beheer-hoek"
        onPointerDown={startGebaar}
        onPointerUp={stopGebaar}
        onPointerCancel={stopGebaar}
        onPointerLeave={stopGebaar}
        aria-hidden="true"
      />

      <AnimatePresence>
        {open && (
          <motion.aside
            className={`kiosk-inst${token && deel === 'monitoring' ? ' kiosk-inst-breed' : ''}`}
            initial={{ x: '102%' }}
            animate={{ x: 0 }}
            exit={{ x: '102%' }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <header className="inst-kop">
              <h2>{!token ? 'Beheer' : deel === 'monitoring' ? 'Monitoring' : 'Scène-instellingen'}</h2>
              <button className="inst-sluit" onClick={() => zetPaneel(false)} aria-label="Sluiten">×</button>
            </header>

            {token && (
              <nav className="inst-tabs">
                <button className={`inst-tab${deel === 'monitoring' ? ' inst-aan' : ''}`} onClick={() => setDeel('monitoring')}>
                  Monitoring
                </button>
                <button className={`inst-tab${deel === 'scene' ? ' inst-aan' : ''}`} onClick={() => setDeel('scene')}>
                  Scène en kiosk
                </button>
                <button className="inst-tab" onClick={() => zetPaneel(false)}>Uitloggen</button>
              </nav>
            )}

            {!token ? (
              <div className="inst-inhoud">
                <Inloggen toetsenbord onIngelogd={zetToken} />
              </div>
            ) : deel === 'monitoring' ? (
              <div className="inst-inhoud">
                <Monitoring token={token} inKiosk onUitgelogd={uitgelogd} />
              </div>
            ) : (
            <>

            {/* Prestatieprofiel (PLAN.md §0.10). Staat boven de tabs omdat het
                de eerste knop is die je op een trage beurs-pc zoekt. */}
            <section className="inst-profiel">
              <span className="inst-label">Prestatieprofiel</span>
              <div className="inst-tabs inst-tabs-plat">
                <button
                  className={`inst-tab${profielBron === 'auto' ? ' inst-aan' : ''}`}
                  onClick={() => meetOpnieuw()}
                >
                  Automatisch
                </button>
                <button
                  className={`inst-tab${profielBron === 'handmatig' && profiel === 'vol' ? ' inst-aan' : ''}`}
                  onClick={() => zetProfiel('vol', 'handmatig')}
                >
                  Vol
                </button>
                <button
                  className={`inst-tab${profielBron === 'handmatig' && profiel === 'licht' ? ' inst-aan' : ''}`}
                  onClick={() => zetProfiel('licht', 'handmatig')}
                >
                  Licht
                </button>
              </div>
              <p className="inst-noot">
                Nu: <b>{profiel === 'vol' ? 'vol' : 'licht'}</b>
                {profielBron === 'auto'
                  ? gemetenFps !== null
                    ? ` — gemeten ${gemetenFps} fps bij het opstarten`
                    : profiel === 'vol' ? ' — meting loopt…' : ' — eerder automatisch gekozen'
                  : ' — handmatig gekozen, de kiosk meet niet meer'}
                . Wordt per machine onthouden.
              </p>
            </section>

            <Offline />

            <nav className="inst-tabs">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  className={`inst-tab${tab === t.id ? ' inst-aan' : ''}`}
                  onClick={() => setTab(t.id)}
                >
                  {t.naam}
                </button>
              ))}
            </nav>

            <div className="inst-inhoud">
              {tab === 'licht' && (
                <>
                  <Schuif label="Omgevingslicht" waarde={l.omgeving} min={0} max={2} onWijzig={(v) => zet('licht', 'omgeving', v)} />
                  <Schuif label="Ambient" waarde={l.ambient} min={0} max={2} onWijzig={(v) => zet('licht', 'ambient', v)} />
                  <Schuif label="Zon (door daklichten)" waarde={l.zonSterkte} min={0} max={12} onWijzig={(v) => zet('licht', 'zonSterkte', v)} />
                  <Kleur label="Zonkleur" waarde={l.zonKleur} onWijzig={(v) => zet('licht', 'zonKleur', v)} />
                  <Schuif label="Armaturen" waarde={l.armatuurSterkte} min={0} max={2} onWijzig={(v) => zet('licht', 'armatuurSterkte', v)} />
                  <Kleur label="Armatuurkleur" waarde={l.armatuurKleur} onWijzig={(v) => zet('licht', 'armatuurKleur', v)} />
                  <Schuif label="Accentlicht" waarde={l.accentSterkte} min={0} max={500} stap={5} onWijzig={(v) => zet('licht', 'accentSterkte', v)} />
                  <Kleur label="Accentkleur" waarde={l.accentKleur} onWijzig={(v) => zet('licht', 'accentKleur', v)} />
                  <Schuif label="Mist begin" waarde={l.mistNabij} min={0} max={120} stap={1} eenheid=" m" onWijzig={(v) => zet('licht', 'mistNabij', v)} />
                  <Schuif label="Mist eind" waarde={l.mistVer} min={20} max={300} stap={1} eenheid=" m" onWijzig={(v) => zet('licht', 'mistVer', v)} />
                  <Kleur label="Mistkleur" waarde={l.mistKleur} onWijzig={(v) => zet('licht', 'mistKleur', v)} />
                  <Schakel label="Schaduwen" aan={l.schaduwen} onWijzig={(v) => zet('licht', 'schaduwen', v)} />
                  <Schuif label="Vullicht armaturen" waarde={l.vulSterkte} min={0} max={3} onWijzig={(v) => zet('licht', 'vulSterkte', v)} />

                  <div className="inst-raster">
                    <Schakel label="Daklichten + zon" aan={l.daklichten} onWijzig={(v) => zet('licht', 'daklichten', v)} />
                    <Schakel label="Lichtschachten" aan={l.lichtschachten} onWijzig={(v) => zet('licht', 'lichtschachten', v)} />
                    <Schakel label="Lichtband gevels" aan={l.lichtband} onWijzig={(v) => zet('licht', 'lichtband', v)} />
                    <Schakel label="Lichtkegels" aan={l.bundels} onWijzig={(v) => zet('licht', 'bundels', v)} />
                    <Schakel label="Lichtvlekken vloer" aan={l.lichtvlekken} onWijzig={(v) => zet('licht', 'lichtvlekken', v)} />
                    <Schakel label="Lensstrepen" aan={l.lensStrepen} onWijzig={(v) => zet('licht', 'lensStrepen', v)} />
                  </div>
                  <Schuif label="Schachtsterkte" waarde={l.schachtSterkte} min={0} max={3} onWijzig={(v) => zet('licht', 'schachtSterkte', v)} />
                  <Schuif label="Daglicht buiten" waarde={l.daglicht} min={0} max={3} onWijzig={(v) => zet('licht', 'daglicht', v)} />
                  <Schuif label="Kegelsterkte" waarde={l.bundelSterkte} min={0} max={3} onWijzig={(v) => zet('licht', 'bundelSterkte', v)} />
                  <p className="inst-noot">
                    De zon valt alleen door de daklichten naar binnen: het dak werpt schaduw.
                    Lichtschachten zijn de duurste post op dit tabblad (een straal per pixel
                    door elke schacht); lensstrepen, kegels en vlekken kosten vrijwel niets.
                  </p>
                  <button className="inst-herstel" onClick={() => herstelGroep('licht')}>Licht herstellen</button>
                </>
              )}

              {tab === 'effecten' && (
                <>
                  <Schakel label="Ambient occlusion" aan={e.ao} onWijzig={(v) => zet('effecten', 'ao', v)} />
                  <Schuif label="AO-sterkte" waarde={e.aoSterkte} min={0} max={6} onWijzig={(v) => zet('effecten', 'aoSterkte', v)} />
                  <Schuif label="AO-straal" waarde={e.aoStraal} min={0.2} max={6} onWijzig={(v) => zet('effecten', 'aoStraal', v)} />
                  <Schakel label="Bloom" aan={e.bloom} onWijzig={(v) => zet('effecten', 'bloom', v)} />
                  <Schuif label="Bloomsterkte" waarde={e.bloomSterkte} min={0} max={3} onWijzig={(v) => zet('effecten', 'bloomSterkte', v)} />
                  <Schuif label="Bloomdrempel" waarde={e.bloomDrempel} min={0} max={1} onWijzig={(v) => zet('effecten', 'bloomDrempel', v)} />
                  <Schakel label="Vignette" aan={e.vignette} onWijzig={(v) => zet('effecten', 'vignette', v)} />
                  <Schuif label="Vignettesterkte" waarde={e.vignetteSterkte} min={0} max={1.5} onWijzig={(v) => zet('effecten', 'vignetteSterkte', v)} />
                  <Schuif label="Belichting" waarde={e.belichting} min={0.4} max={3} onWijzig={(v) => zet('effecten', 'belichting', v)} />
                  <Schuif label="Contrast" waarde={e.contrast} min={-0.5} max={0.8} onWijzig={(v) => zet('effecten', 'contrast', v)} />
                  <Schuif label="Verzadiging" waarde={e.verzadiging} min={-1} max={1} onWijzig={(v) => zet('effecten', 'verzadiging', v)} />
                  <Schuif label="Gradatie" waarde={e.gradatie} min={0} max={1} onWijzig={(v) => zet('effecten', 'gradatie', v)} />
                  <Kleur label="Schaduwtint" waarde={e.schaduwTint} onWijzig={(v) => zet('effecten', 'schaduwTint', v)} />
                  <Kleur label="Lichttint" waarde={e.lichtTint} onWijzig={(v) => zet('effecten', 'lichtTint', v)} />
                  <Schuif label="Alleen merkrood" waarde={e.merkrood} min={0} max={1} onWijzig={(v) => zet('effecten', 'merkrood', v)} />
                  <Schuif label="Filmkorrel" waarde={e.korrel} min={0} max={0.12} stap={0.005} onWijzig={(v) => zet('effecten', 'korrel', v)} />
                  <Schuif label="Kleurschifting" waarde={e.aberratie} min={0} max={0.02} stap={0.0005} onWijzig={(v) => zet('effecten', 'aberratie', v)} />
                  <Schakel label="Scherptediepte (hotspots)" aan={e.dof} onWijzig={(v) => zet('effecten', 'dof', v)} />
                  <Schuif label="Bokeh" waarde={e.dofBokeh} min={0} max={6} onWijzig={(v) => zet('effecten', 'dofBokeh', v)} />
                  <Schuif label="Scherp bereik" waarde={e.dofBereik} min={0.2} max={3} onWijzig={(v) => zet('effecten', 'dofBereik', v)} />
                  <Schakel label="Randverzachting (SMAA)" aan={e.smaa} onWijzig={(v) => zet('effecten', 'smaa', v)} />
                  <p className="inst-noot">
                    Belichting, contrast en verzadiging werken in de AgX-curve, vóór de gradatie:
                    hooglichten lopen zacht dicht in plaats van af te kappen. Korrel en
                    kleurschifting zijn gratis; scherptediepte kost een halve-resolutiepass.
                  </p>
                  <button className="inst-herstel" onClick={() => herstelGroep('effecten')}>Effecten herstellen</button>
                </>
              )}

              {tab === 'oppervlak' && (
                <>
                  <Schakel label="Texturen" aan={op.texturen} onWijzig={(v) => zet('oppervlak', 'texturen', v)} />
                  <Schuif
                    label="Textuurdetail" waarde={op.textuurDetail} min={256} max={2048} stap={256} eenheid=" px"
                    onWijzig={(v) => zet('oppervlak', 'textuurDetail', v)}
                  />
                  <Schuif label="Reliëf" waarde={op.relief} min={0} max={3} onWijzig={(v) => zet('oppervlak', 'relief', v)} />
                  <Schuif label="Variatie per stuk" waarde={op.variatie} min={0} max={0.4} onWijzig={(v) => zet('oppervlak', 'variatie', v)} />

                  <Schakel label="Vloerreflectie" aan={op.vloerReflectie} onWijzig={(v) => zet('oppervlak', 'vloerReflectie', v)} />
                  <Schuif
                    label="Reflectiesterkte" waarde={op.reflectieSterkte} min={0} max={1}
                    onWijzig={(v) => zet('oppervlak', 'reflectieSterkte', v)}
                  />
                  <Schuif
                    label="Reflectievervaging" waarde={op.reflectieVervaging} min={0} max={2}
                    onWijzig={(v) => zet('oppervlak', 'reflectieVervaging', v)}
                  />
                  <Schuif
                    label="Reflectiebuffer" waarde={op.reflectieResolutie} min={128} max={1024} stap={128} eenheid=" px"
                    onWijzig={(v) => zet('oppervlak', 'reflectieResolutie', v)}
                  />

                  <Schakel
                    label="Zachte schaduwen" aan={op.zachteSchaduwen}
                    onWijzig={(v) => zet('oppervlak', 'zachteSchaduwen', v)}
                  />

                  {/* De drie duurste schuiven van de hele Studio staan op dit
                      tabblad, dus ze staan er met hun prijs erbij. Wie op de
                      generale moet snijden, snijdt hier en in deze volgorde. */}
                  <p className="inst-noot">
                    Kosten, van duur naar goedkoop: vloerreflectie (tweede scènerender),
                    zachte schaduwen (16 extra bemonsteringen per schaduwpixel),
                    textuurdetail (laadtijd en VRAM). Variatie en reliëf kosten niets.
                  </p>

                  <button className="inst-herstel" onClick={() => herstelGroep('oppervlak')}>Oppervlak herstellen</button>
                </>
              )}

              {tab === 'magazijn' && (
                <>
                  <Schuif label="Gangpaden" waarde={m.gangpaden} min={2} max={9} stap={1} onWijzig={(v) => zet('magazijn', 'gangpaden', v)} />
                  <Schuif label="Bays per wand" waarde={m.bays} min={6} max={30} stap={1} onWijzig={(v) => zet('magazijn', 'bays', v)} />
                  <Schuif label="Niveaus" waarde={m.niveaus} min={2} max={7} stap={1} onWijzig={(v) => zet('magazijn', 'niveaus', v)} />
                  <Schuif label="Vulgraad" waarde={m.vulgraad} min={0} max={1} onWijzig={(v) => zet('magazijn', 'vulgraad', v)} />
                  <Schuif label="Seed" waarde={m.seed} min={1} max={99999} stap={1} onWijzig={(v) => zet('magazijn', 'seed', v)} />
                  <Schuif label="Aantal trucks" waarde={o.trucks} min={0} max={9} stap={1} onWijzig={(v) => zet('objecten', 'trucks', v)} />
                  <p className="inst-hint">
                    Het magazijn wordt bij elke wijziging opnieuw opgebouwd. De seed bepaalt de
                    verdeling van de lading — dezelfde seed geeft altijd hetzelfde magazijn.
                  </p>
                  <button className="inst-herstel" onClick={() => herstelGroep('magazijn')}>Magazijn herstellen</button>
                </>
              )}

              {tab === 'objecten' && (
                <>
                  <div className="inst-raster">
                    {objectRijen.map(([sleutel, naam]) => (
                      <Schakel
                        key={sleutel}
                        label={naam}
                        aan={o[sleutel] as boolean}
                        onWijzig={(v) => zet('objecten', sleutel, v as never)}
                      />
                    ))}
                  </div>
                  <button className="inst-herstel" onClick={() => herstelGroep('objecten')}>Objecten herstellen</button>
                </>
              )}

              {tab === 'materialen' && (
                <>
                  <Kleur label="Staal (stellingen)" waarde={mt.staal} onWijzig={(v) => zet('materialen', 'staal', v)} />
                  <Kleur label="Liggers" waarde={mt.ligger} onWijzig={(v) => zet('materialen', 'ligger', v)} />
                  <Kleur label="Vloer" waarde={mt.vloer} onWijzig={(v) => zet('materialen', 'vloer', v)} />
                  <Kleur label="Pallets" waarde={mt.pallet} onWijzig={(v) => zet('materialen', 'pallet', v)} />
                  <Kleur label="Karton" waarde={mt.karton} onWijzig={(v) => zet('materialen', 'karton', v)} />
                  <Kleur label="Truck rood" waarde={mt.truckRood} onWijzig={(v) => zet('materialen', 'truckRood', v)} />
                  <Kleur label="Truck antraciet" waarde={mt.truckZwart} onWijzig={(v) => zet('materialen', 'truckZwart', v)} />
                  <button className="inst-herstel" onClick={() => herstelGroep('materialen')}>Kleuren herstellen</button>
                </>
              )}

              {tab === 'animatie' && (
                <>
                  <Schakel label="Animatie aan" aan={a.aan} onWijzig={(v) => zet('animatie', 'aan', v)} />
                  <Schuif label="Snelheid" waarde={a.snelheid} min={0} max={4} onWijzig={(v) => zet('animatie', 'snelheid', v)} />
                  <div className="inst-raster">
                    {animatieRijen.map(([sleutel, naam]) => (
                      <Schakel
                        key={sleutel}
                        label={naam}
                        aan={a[sleutel] as boolean}
                        onWijzig={(v) => zet('animatie', sleutel, v as never)}
                      />
                    ))}
                  </div>
                  <button className="inst-herstel" onClick={() => herstelGroep('animatie')}>Animatie herstellen</button>
                </>
              )}
            </div>

            <footer className="inst-voet">
              <button className="inst-actie" onClick={kopieer}>
                {gekopieerd ? 'Gekopieerd' : 'Exporteer JSON'}
              </button>
              <button className="inst-actie inst-secundair" onClick={herstel}>Alles herstellen</button>
            </footer>
            </>
            )}
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  )
}
